import { useEffect } from 'react';

import { ROLE_AUTHORISATION_MODULE_IDS } from '@xemplo/gp-api';
import { PAYRUN_COMPONENTS } from '@xemplo/gp-constants';
import { useModalDialog } from '@xemplo/modal-dialog';
import { useRoleAuthorisation } from '@xemplo/roles-query';

import { useClosePrompt } from './modal/use-close-prompt';
import { useCreateAmendmentForm } from './modal/use-create-amendment-form';
import { CreateAmendmentProps } from './create-amendment.types';

type UseCreateAmendmentProps = Readonly<
  CreateAmendmentProps & { modalDialog: ReturnType<typeof useModalDialog> }
>;

export const useCreateAmendment = (props: UseCreateAmendmentProps) => {
  const { modalDialog, onSuccess, payrunModeValues } = props;
  const { requestClose, confirmationPrompt } = useClosePrompt({
    closeDialog: modalDialog.closeDialog,
  });

  const { methods, getFormStep, primaryButton, tertiaryButton, resetForm } =
    useCreateAmendmentForm({
      onRequestClose: requestClose,
      onClose: modalDialog.closeDialog,
      onSuccess,
      payrunModeValues,
      selectedWorkers: payrunModeValues?.selectedWorkers,
    });

  // The form hook outlives the modal body, so state must be cleared on every
  // close path (Esc and backdrop click included) — keyed off isOpen as the
  // single signal common to all of them.
  useEffect(() => {
    if (!modalDialog.isOpen) resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalDialog.isOpen]);

  //NOTE: We don't currenlty have a role authorisation object setup for Amendments. The payrun create amendmnet has the same permissions so we are using that for now.
  // Once BE has updated the amendments object we can update this to use amendments module id. https://expedo.atlassian.net/browse/FD-1365
  const { componentVisible } = useRoleAuthorisation(ROLE_AUTHORISATION_MODULE_IDS.payrun);
  const showButton = componentVisible(PAYRUN_COMPONENTS.AMENDMENT_CREATE);

  return {
    handleClose: requestClose,
    methods,
    getFormStep,
    primaryButton,
    tertiaryButton,
    confirmationPrompt,
    showButton,
  };
};
